import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Fade,
  FormControlLabel,
  Grid,
  Modal,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils/string_utils';
import { useTranslation } from 'react-i18next';
import { MdOutlineTireRepair } from 'react-icons/md';
import { ReactComponent as Tire } from 'assets/svg/home/tire.svg';
import { ReactComponent as Tire3 } from 'assets/svg/home/tire_3.svg';
import * as useClasses from 'pages/Home/const';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setParameters } from 'redux/fleets/ducks';
import PropTypes from 'prop-types';
import { getOverviewChart } from 'services/fleet_service';
import OverviewChart from './OverviewChart';
import { formatNumberPercent } from 'utils/helper';

const useStyles = makeStyles()(() => {
  return {
    tileStyles: {
      display: 'flex',
      flexDirection: 'row',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      padding: '16px 32px 16px 16px',
      borderRadius: 12,
      border: '1px solid var(--default-gray-200, #E5E7EB)',
    },
    iconStyles: {
      height: 40,
      width: 40,
    },
    container: {
      transition: 'transform 0.2s ease-in-out',
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.05)',
      },
    },
  };
});
const metric_map = {
  IN_USE: 'IN_USE',
  less_3mm: 'LESS_THAN_3MM',
  '3_5_mm': 'BETWEEN_3_5_mm',
  less_3mon: 'LESS_THAN_3_MONTHS',
  GREATER_THAN_12_MONTHS: 'GREATER_THAN_12_MONTHS',
  IRREGULAR_WEAR: 'IRREGULAR_WEAR',
  OPTIMUM_AIR_PRESSURE: 'OPTIMUM_AIR_PRESSURE',
};
export const TireOverview = ({
  isLoading,
  Count,
  tiresByDepth,
  outServicing,
}) => {
  const { t } = useTranslation();

  const { classes: new_classes } = useStyles();
  const theme = useTheme();

  const iconConfig = {
    fill: theme.palette.primary.main,
  };
  const chart_title = {
    IN_USE: t('tireInventoryItems.statuses.IN_USE'),
    less_3mm: '< 3mm',
    '3_5_mm': '3-5 mm',
    less_3mon: t('homePage.lessThan3Months'),
    GREATER_THAN_12_MONTHS: t('homePage.moreThan12months'),
    IRREGULAR_WEAR: t('common.irregular_wear'),
    OPTIMUM_AIR_PRESSURE: t('common.optimum_air_pressure'),
  };
  const { classes } = useClasses.style();
  const [chartTitle, setChartTitle] = useState(null);
  const [dataSets, setDataSets] = useState(null);
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [flag, setFlag] = useState(false);
  const [location_, setLocation] = useState(null);
  const overviewDetails = useSelector(state => state.fleet.tireOverview);
  const [days, setDays] = useState(30);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleChange = event => {
    setChecked(event.target.checked);
  };
  const handleClose = () => {
    setDays(30);
    setLocation(null);
    setFlag(false);
    setDataSets(null);
    setOpen(false);
  };

  const renderLoading = () => {
    return (
      <Box
        display='flex'
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        width='100%'
        height='100%'
      >
        <Count
          icon={<MdOutlineTireRepair size={60} {...iconConfig} />}
          isLoading={isLoading}
        />
      </Box>
    );
  };

  const renderNoTyreState = () => {
    return (
      <Typography
        variant='h5'
        component='span'
        className={classes.manageTiresText}
      >
        {t('tireOverviewPage.noTireMessage')}
      </Typography>
    );
  };

  const Lessthan3months = () => {
    if (overviewDetails)
      return (
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          onClick={() => categoryClickHandler('less_3mon', 4)}
          className={new_classes.container}
        >
          <Box className={new_classes.tileStyles}>
            <Tooltip
              title={
                <>
                  <h5>{t('homePage.lessThan3Months')}</h5>
                  <ul className={classes.list}>
                    {outServicing.lessThan3months.map(item => (
                      <li key={item.tire_id}>
                        <Link
                          to={`${ROUTES.TIRE_INVENTORY}/${item.fleet_id}/${item.system_tire_id}`}
                          className={classes.tireLink}
                        >
                          {item.tire_id}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              }
              placement='right'
              interactive
            >
              <Box>
                <Tire fill={theme.palette.primary.main} alt='tire' />
              </Box>
            </Tooltip>
            <Box mx='1rem' style={{ width: '150px' }}>
              <Typography
                variant='h6'
                gutterBottom
                color='textSecondary'
                align='center'
              >
                {t('homePage.lessThan3Months')}
              </Typography>
              <Typography
                variant='subtitle1'
                fontWeight='bold'
                gutterBottom
                align='center'
              >
                {overviewDetails.within_3mon}
              </Typography>
            </Box>
          </Box>
        </Grid>
      );
  };
  // eslint-disable-next-line
  const optimumPress = () => {
    if (overviewDetails)
      return (
        <Grid
          item
          xs={12}
          md={6}
          lg={4}
          onClick={() => categoryClickHandler('optimum_air_pressure', 5)}
          className={new_classes.container}
        >
          <Box className={new_classes.tileStyles}>
            <Box>
              <Tire fill={theme.palette.primary.main} alt='tire' />
            </Box>
            <Box mx='1rem' style={{ width: '150px' }}>
              <Typography
                variant='h6'
                gutterBottom
                color='textSecondary'
                align='center'
              >
                {t('common.air_pressure_analytics')}
              </Typography>
              <Typography
                variant='subtitle1'
                fontWeight='bold'
                gutterBottom
                align='center'
              >
                {overviewDetails.optimumAirPressure === 'N/A'
                  ? 'N/A'
                  : formatNumberPercent(overviewDetails.optimumAirPressure)}
              </Typography>
            </Box>
          </Box>
        </Grid>
      );
  };

  const fetchOverviewChart = async location => {
    let params = {
      section: 'tire_overview',
      metric_name: metric_map[location],
      days: parseInt(days),
    };
    try {
      const res = await getOverviewChart(params);
      if (res?.status === 200) {
        let resources = res?.data?.resources;

        let xAxis = resources?.x;
        let yAxis = resources?.y;
        let temp = [];
        for (let i = 0; i < xAxis.length; i++) {
          let data = 0;
          if (yAxis[i]) {
            data = yAxis[i];
          }
          temp.push({
            name: xAxis[i],
            count: data,
          });
        }
        setDataSets(temp);
      }
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    if (metric_map[location_] && parseInt(days)) {
      fetchOverviewChart(location_);
    }
    // eslint-disable-next-line
  }, [days]);
  const categoryClickHandler = async (location, value) => {
    setLocation(location);
    if (!checked) {
      dispatch(
        setParameters({
          tireTabValue: value,
          tireLocation: location,
          accessing: 'ONLY_TIRE',
        })
      );
      navigate('/overview/tire');
    } else if (checked) {
      if (metric_map[location]) {
        fetchOverviewChart(location);
      } else {
        setFlag(true);
      }
      setChartTitle(chart_title[location]);
      setOpen(true);
    }
  };

  const hasAnyCountGreaterThanZero = Object.values(overviewDetails || {}).some(
    item => item > 0
  );

  const renderTyreOverview = () => {
    if (isLoading) {
      return renderLoading();
    }
    if (!overviewDetails || !hasAnyCountGreaterThanZero) {
      return renderNoTyreState();
    }
    if (overviewDetails && hasAnyCountGreaterThanZero) {
      return (
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            onClick={() => categoryClickHandler('IN_USE', 0)}
            className={new_classes.container}
          >
            <Box className={new_classes.tileStyles}>
              <Box>
                <Tire fill={theme.palette.primary.main} alt='tire' />
              </Box>
              <Box mx='1rem' style={{ width: '150px' }}>
                <Typography
                  variant='h6'
                  gutterBottom
                  color='textSecondary'
                  data-hook='tires_in_use'
                  align='center'
                >
                  {t('common.totalTiresInUse')}
                </Typography>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  gutterBottom
                  align='center'
                >
                  {overviewDetails.tires_in_use}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            onClick={() => categoryClickHandler('less_3mm', 1)}
            className={new_classes.container}
          >
            <Box className={new_classes.tileStyles}>
              <Tooltip
                title={
                  <ul className={classes.list}>
                    {tiresByDepth.tiresBelow3.map(item => (
                      <li key={item.tire_id}>
                        <Link
                          to={`${ROUTES.TIRE_INVENTORY}/${item.fleet_id}/${item.system_tire_id}`}
                          className={classes.tireLink}
                        >
                          {item.tire_id}
                        </Link>
                      </li>
                    ))}
                  </ul>
                }
                placement='right'
                interactive
                disableHoverListener={tiresByDepth.tiresBelow3.length === 0}
              >
                <Box>
                  <Tire3 fill={theme.palette.primary.main} alt='tire' />
                </Box>
              </Tooltip>
              <Box mx='1rem' style={{ width: '150px' }}>
                <Typography
                  variant='h6'
                  gutterBottom
                  color='textSecondary'
                  align='center'
                >
                  {`< 3mm`}
                </Typography>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  gutterBottom
                  align='center'
                >
                  {overviewDetails.less_3mm}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            onClick={() => categoryClickHandler('3_5_mm', 2)}
            className={new_classes.container}
          >
            <Box className={new_classes.tileStyles}>
              <Tooltip
                title={
                  <ul className={classes.list}>
                    {tiresByDepth?.tiresBetween3and5.map(item => (
                      <li key={item.tire_id}>
                        <Link
                          to={`${ROUTES.TIRE_INVENTORY}/${item.fleet_id}/${item.system_tire_id}`}
                          className={classes.tireLink}
                        >
                          {item.tire_id}
                        </Link>
                      </li>
                    ))}
                  </ul>
                }
                placement='right'
                interactive
                disableHoverListener={
                  tiresByDepth?.tiresBetween3and5?.length === 0
                }
              >
                <Box>
                  <Tire fill={theme.palette.primary.main} alt='tire' />
                </Box>
              </Tooltip>
              <Box mx='1rem' style={{ width: '150px' }}>
                <Typography
                  variant='h6'
                  gutterBottom
                  color='textSecondary'
                  align='center'
                >
                  {`3-5mm`}
                </Typography>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  gutterBottom
                  align='center'
                >
                  {overviewDetails.between_3mm_5mm}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            onClick={() => categoryClickHandler('grooves_td_diff_gt_2', 3)}
            className={new_classes.container}
          >
            <Box className={new_classes.tileStyles}>
              <Tooltip
                placement='right'
                interactive
                disableHoverListener={
                  overviewDetails.grooves_td_diff_gt_2 === 0
                }
              >
                <Box>
                  <Tire fill={theme.palette.primary.main} alt='tire' />
                </Box>
              </Tooltip>
              <Box mx='1rem'>
                <Typography
                  variant='h6'
                  gutterBottom
                  color='textSecondary'
                  align='center'
                >
                  {t('common.irregular_wear')}
                </Typography>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  gutterBottom
                  align='center'
                >
                  {overviewDetails.grooves_td_diff_gt_2}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {Lessthan3months()}
          {optimumPress()}
        </Grid>
      );
    }
    return <></>;
  };

  return (
    <Paper
      style={{
        height: '100%',
        padding: '16px 20px',
        boxShadow: '0px 2px 16px 0px rgba(179, 179, 179, 0.10)',
      }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <Typography variant='subtitle1' gutterBottom fontWeight={900}>
          {t('common.tireOverview')}
        </Typography>
        <FormControlLabel
          value='top'
          control={
            <Switch color='primary' checked={checked} onChange={handleChange} />
          }
          label={t('common.graphical_mode')}
          labelPlacement='end'
        />
      </Box>
      <Box>{renderTyreOverview()}</Box>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          border: 'none',
        }}
      >
        <Fade in={open}>
          <Card sx={{ minWidth: 600, border: 'none' }}>
            <CardContent>
              {!flag && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant='h5'
                      gutterBottom
                      fontWeight={900}
                      align={'center'}
                    >
                      {chartTitle}
                    </Typography>
                  </Box>
                  <Box sx={{ float: 'right' }}>
                    <TextField
                      id='outlined-basic'
                      sx={{ maxWidth: 100 }}
                      label='Days'
                      type='number'
                      variant='outlined'
                      value={days}
                      InputProps={{
                        inputProps: {
                          min: 1,
                        },
                      }}
                      onChange={e => {
                        let val = e.target.value;
                        setTimeout(() => setDays(val), 700);
                      }}
                    />
                  </Box>
                </Box>
              )}

              {dataSets?.length && <OverviewChart dataSets={dataSets} />}
              {flag && (
                <Alert severity='error'>
                  <AlertTitle>{t('common.data_not_present')}</AlertTitle>
                  {t('common.no_graphical_data')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </Paper>
  );
};

TireOverview.propTypes = {
  outServicing: PropTypes.object,
  tiresByDepth: PropTypes.object,
  isLoading: PropTypes.bool,
  Count: PropTypes.elementType,
};
